import { chakra, Flex, Box } from '@chakra-ui/react';
import type { UseQueryResult } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';

import type { TimeChartData } from 'ui/shared/chart/types';

import ContentLoader from 'ui/shared/ContentLoader';
import DataFetchAlert from 'ui/shared/DataFetchAlert';
import ChainIndicatorChart from './ChainIndicatorChart';

type Props = UseQueryResult<TimeChartData> & {
  indicator: any;
};

const ChainIndicatorChartContainer = ({
  data,
  isError,
  isPending,
  indicator
}: Props) => {

  const [customData, setCustomData] = useState<TimeChartData | null>(null);
  const [loadingCustom, setLoadingCustom] = useState(false);
  const [errorCustom, setErrorCustom] = useState(false);

  /*
  |--------------------------------------------------------------------------
  | Custom API for coin_price
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    if (!["coin_price", "market_cap"].includes(indicator?.id)) {
      setCustomData(null);
      return;
    }

    const fetchMarketData = async () => {
      try {

        setLoadingCustom(true);

        const res = await fetch(
          "https://dexchart.dancharts.com/token/price/charts/market"
        );

        const json: any = await res.json();

        /*
        |--------------------------------------------------------------------------
        | Choose Value Field
        |--------------------------------------------------------------------------
        */

        const valueKey =
          indicator?.id === "market_cap"
            ? "market_cap"
            : "closing_price";

        const chartName =
          indicator?.id === "market_cap"
            ? "Market Cap"
            : "DAN Price";

        /*
        |--------------------------------------------------------------------------
        | Format Data
        |--------------------------------------------------------------------------
        */

        const formatted: TimeChartData = [
          {
            name: chartName,

            valueFormatter: (x: number) =>
              '$' + x.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 6
              }),

            items: json.chart_data
              .slice()
              .sort((a: any, b: any) =>
                new Date(a.date).getTime() -
                new Date(b.date).getTime()
              )
              .map((row: any) => ({
                date: new Date(row.date),
                value: Number(row[valueKey])
              }))
          }
        ];

        setCustomData(formatted);
        setErrorCustom(false);

      } catch (e) {

        console.error(e);
        setErrorCustom(true);

      } finally {

        setLoadingCustom(false);

      }

    };

    fetchMarketData();

  }, [indicator]);

  /*
  |--------------------------------------------------------------------------
  | Choose Data Source
  |--------------------------------------------------------------------------
  */

  const useCustom =
    ["coin_price", "market_cap"].includes(indicator?.id);

  const finalData = useCustom ? customData : data;
  const finalLoading = useCustom ? loadingCustom : isPending;
  const finalError = useCustom ? errorCustom : isError;


  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */

  const content = (() => {

    if (finalLoading) {
      return <ContentLoader mt="auto" fontSize="xs" />;
    }

    if (finalError) {
      return <DataFetchAlert fontSize="xs" p={3} />;
    }

    if (!finalData || finalData[0].items.length === 0) {
      return <chakra.span fontSize="xs">no data</chakra.span>;
    }

    return (
      <Box mx="-10px" my="-5px" h="calc(100% + 10px)" w="calc(100% + 20px)">
        <ChainIndicatorChart data={finalData} />
      </Box>
    );

  })();

  return (
    <Flex
      h={{ base: '80px', lg: '110px' }}
      alignItems="flex-start"
      flexGrow={1}
    >
      {content}
    </Flex>
  );
};

export default React.memo(ChainIndicatorChartContainer);